import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { DomainBadge } from "@/components/DomainBadge";
import { KioskGate } from "@/components/KioskGate";

import PublicHomePage from "@/pages/PublicHomePage";
import PublicInventoryPage from "@/pages/PublicInventoryPage";
import PublicVehiclePage from "@/pages/PublicVehiclePage";
import FinancingPage from "@/pages/FinancingPage";
import TradeInPage from "@/pages/TradeInPage";
import ServicePage from "@/pages/ServicePage";
import ContactPage from "@/pages/ContactPage";
import ReviewsPage from "@/pages/ReviewsPage";
import InstallPage from "@/pages/InstallPage";
import VerifyPage from "@/pages/VerifyPage";
import EstimatePage from "@/pages/EstimatePage";
import AuctionsPage from "@/pages/AuctionsPage";
import AuctionDetailPage from "@/pages/AuctionDetailPage";
import AuctionRegisterPage from "@/pages/AuctionRegisterPage";

import LoginPage from "@/pages/LoginPage";
import KioskHomePage from "@/pages/KioskHomePage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import CheckoutWizard from "@/pages/CheckoutWizard";
import TestDriveWaiverPage from "@/pages/TestDriveWaiverPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminCRM from "@/pages/admin/AdminCRM";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminDeals from "@/pages/admin/AdminDeals";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminService from "@/pages/admin/AdminService";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAuctions from "@/pages/admin/AdminAuctions";

const STAFF_ROLES = new Set([
  "admin", "manager", "sales_rep", "finance_manager",
  "service_advisor", "registration_clerk",
]);

function StaffRoute({ children }) {
  const { user, checked } = useAuth();
  if (!checked) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user || !STAFF_ROLES.has(user.role)) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* === Public website === */}
          <Route path="/" element={<PublicHomePage />} />
          <Route path="/inventory" element={<PublicInventoryPage />} />
          <Route path="/inventory/:id" element={<PublicVehiclePage />} />
          <Route path="/financing" element={<FinancingPage />} />
          <Route path="/trade-in" element={<TradeInPage />} />
          <Route path="/service" element={<ServicePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/install" element={<InstallPage />} />
          <Route path="/verify/:id" element={<VerifyPage />} />
          <Route path="/estimate" element={<EstimatePage />} />

          {/* === Auction portal === */}
          <Route path="/auctions" element={<AuctionsPage />} />
          <Route path="/auctions/register" element={<AuctionRegisterPage />} />
          <Route path="/auctions/:id" element={<AuctionDetailPage />} />

          {/* === Customer Kiosk (locked tablet) === */}
          <Route path="/kiosk-launch" element={<KioskGate />} />
          <Route path="/kiosk" element={<KioskGate><KioskHomePage /></KioskGate>} />
          <Route path="/kiosk/vehicle/:id" element={<KioskGate><VehicleDetailPage /></KioskGate>} />
          <Route path="/kiosk/checkout/:vehicleId" element={<KioskGate><CheckoutWizard /></KioskGate>} />
          <Route path="/kiosk/test-drive/:vehicleId" element={<KioskGate><TestDriveWaiverPage /></KioskGate>} />
          <Route path="/kiosk/payment/success" element={<KioskGate><PaymentSuccessPage /></KioskGate>} />

          {/* === Staff === */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin" element={<StaffRoute><AdminDashboard /></StaffRoute>} />
          <Route path="/admin/inventory" element={<StaffRoute><AdminInventory /></StaffRoute>} />
          <Route path="/admin/crm" element={<StaffRoute><AdminCRM /></StaffRoute>} />
          <Route path="/admin/customers" element={<StaffRoute><AdminCustomers /></StaffRoute>} />
          <Route path="/admin/customers/:id" element={<StaffRoute><AdminCustomerDetail /></StaffRoute>} />
          <Route path="/admin/documents" element={<StaffRoute><AdminDocuments /></StaffRoute>} />
          <Route path="/admin/deals" element={<StaffRoute><AdminDeals /></StaffRoute>} />
          <Route path="/admin/finance" element={<StaffRoute><AdminFinance /></StaffRoute>} />
          <Route path="/admin/service" element={<StaffRoute><AdminService /></StaffRoute>} />
          <Route path="/admin/users" element={<StaffRoute><AdminUsers /></StaffRoute>} />
          <Route path="/admin/reports" element={<StaffRoute><AdminReports /></StaffRoute>} />
          <Route path="/admin/auctions" element={<StaffRoute><AdminAuctions /></StaffRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" theme="dark" />
        <DomainBadge />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
